/*
    The war look's noise without Pixi (0.98.8: the bakes' worker, managers/atlasReadWorker.ts, lays the snow's patches
    by it, and a worker has no Pixi): the bytes and the lookups, as they were in warNoise.ts, which makes the textures
    of them and passes these on.
*/

/**
 * One 256x256 RGBA texture of seamless (tileable) noise, sampled with repeat wrapping by every War Look
 * shader. Reading this texture is far cheaper than computing fractal noise per pixel on the GPU.
 *   R: smooth fractal noise (5 octaves)        - large patches, fog, gas billows
 *   G: ridged fractal noise                    - churned mud ridges, ruts, cloud edges
 *   B: fine value noise (2 high octaves)       - grain, pebbles, ripples
 *   A: a second, decorrelated fractal noise    - zones (wet areas, tufts) and domain warping
 */
export const NOISE_SIZE = 256;
let noiseData: Uint8Array | undefined;

/** the raw noise bytes (RGBA, NOISE_SIZE x NOISE_SIZE), generated once */
export function generateNoiseData(size = NOISE_SIZE, seed = 9173): Uint8Array {
    if (noiseData) return noiseData;
    const hash = (x: number, y: number, salt: number): number => {
        let h = (x * 374761393 + y * 668265263 + salt * 2147483647 + seed * 1274126177) | 0;
        h = Math.imul(h ^ (h >>> 13), 1274126177);
        h ^= h >>> 16;
        return (h >>> 0) / 4294967296;
    };
    const fade = (t: number): number => t * t * (3 - 2 * t);
    // periodic value noise: the lattice wraps every `period` cells, so the texture tiles seamlessly
    const valueNoise = (x: number, y: number, period: number, salt: number): number => {
        const fx = x * period, fy = y * period;
        const ix = Math.floor(fx), iy = Math.floor(fy);
        const tx = fade(fx - ix), ty = fade(fy - iy);
        const x0 = ((ix % period) + period) % period, x1 = (x0 + 1) % period;
        const y0 = ((iy % period) + period) % period, y1 = (y0 + 1) % period;
        const a = hash(x0, y0, salt), b = hash(x1, y0, salt), c = hash(x0, y1, salt), d = hash(x1, y1, salt);
        return a + (b - a) * tx + (c - a) * ty + (a - b - c + d) * tx * ty;
    };
    const fbm = (x: number, y: number, basePeriod: number, octaves: number, salt: number): number => {
        let sum = 0, amp = 0.5, norm = 0, period = basePeriod;
        for (let o = 0; o < octaves; o++) {
            sum += amp * valueNoise(x, y, period, salt + o * 31);
            norm += amp; amp *= 0.5; period *= 2;
        }
        return sum / norm;
    };
    const data = new Uint8Array(size * size * 4);
    const contrast = (v: number, k: number): number => Math.min(1, Math.max(0, (v - 0.5) * k + 0.5));
    for (let py = 0; py < size; py++) {
        for (let px = 0; px < size; px++) {
            const x = px / size, y = py / size;
            const r = contrast(fbm(x, y, 4, 5, 11), 1.7);
            const ridged = 1 - Math.abs(2 * fbm(x, y, 4, 5, 57) - 1);
            const g = contrast(ridged, 1.35);
            const b = contrast(0.6 * valueNoise(x, y, 64, 91) + 0.4 * valueNoise(x, y, 128, 97), 1.4);
            const a = contrast(fbm(x, y, 4, 5, 131), 1.7);
            const i = (py * size + px) * 4;
            data[i] = r * 255; data[i + 1] = g * 255; data[i + 2] = b * 255; data[i + 3] = a * 255;
        }
    }
    return noiseData = data;
}

/**
 * The same lookup the shaders do (repeat wrapping, bilinear), on the CPU: lets decal placement
 * agree exactly with where the ground shader draws mud. `channel` 0..3 = R, G, B, A.
 */
export function sampleNoise(u: number, v: number, channel: number): number {
    const data = generateNoiseData();
    const size = NOISE_SIZE;
    const x = (((u % 1) + 1) % 1) * size - 0.5, y = (((v % 1) + 1) % 1) * size - 0.5;
    const x0 = Math.floor(x), y0 = Math.floor(y), tx = x - x0, ty = y - y0;
    const at = (ix: number, iy: number): number => data[((((iy % size) + size) % size) * size + (((ix % size) + size) % size)) * 4 + channel] / 255;
    const a = at(x0, y0), b = at(x0 + 1, y0), c = at(x0, y0 + 1), d = at(x0 + 1, y0 + 1);
    return a + (b - a) * tx + (c - a) * ty + (a - b - c + d) * tx * ty;
}

/** how muddy the ground is at a world position: 0 = grassland, 1 = shelled mud (matches GROUND_FRAGMENT) */
export function mudZone(x: number, y: number): number {
    const n = sampleNoise(x * 0.0042 + 0.31, y * 0.0042 + 0.77, 0) + (sampleNoise(x * 0.019 + 0.5, y * 0.019 + 0.2, 0) - 0.5) * 0.14;
    const t = Math.min(1, Math.max(0, (n - 0.5) / 0.15));
    return t * t * (3 - 2 * t);
}
